import { BellOutlined, DisconnectOutlined, PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Badge, Button, Drawer, Empty, Progress, Space, Tag, Tooltip, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TaskSnapshot } from '../api/tasks';
import { useTasks } from '../hooks/use-tasks';
import { useCancelTaskMutation } from '../pages/tasks/providers/use-task-mutations';
import {
  formatProgress,
  formatRelativeTime,
  isActiveStatus,
  isTerminalStatus,
  STATUS_COLOR,
} from '../pages/tasks/task-utils';

const { Text, Title } = Typography;

export const TaskCenter = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const cancelMutation = useCancelTaskMutation();
  const { data: tasks = [], isFetching } = useTasks({ driver: false });

  const activeCount = tasks.filter(task => isActiveStatus(task.status)).length;

  const sortedTasks = useMemo(() => {
    // eslint-disable-next-line react-hooks/purity -- Date.now() is intentionally called during render to filter tasks based on current time
    const now = Date.now();
    const terminalCutoffMs = 5 * 60 * 1000; // drop terminal tasks older than 5 minutes
    return [...tasks]
      .filter(task => {
        if (!isTerminalStatus(task.status)) {
          return true;
        }
        const created = task.createdAt ? Date.parse(task.createdAt) : 0;
        return created === 0 || now - created <= terminalCutoffMs;
      })
      .sort((a, b) => {
        const aTime = a.createdAt ?? '';
        const bTime = b.createdAt ?? '';
        return bTime.localeCompare(aTime);
      });
  }, [tasks]);

  const handleCancel = (taskId: string) => {
    cancelMutation.mutate(taskId);
  };

  const renderActions = (task: TaskSnapshot) => {
    const actions: React.ReactNode[] = [];
    if (isActiveStatus(task.status)) {
      actions.push(
        <Tooltip key='cancel' title='Request cancel'>
          <Button
            size='small'
            icon={<PauseCircleOutlined />}
            loading={cancelMutation.isPending}
            onClick={event => {
              event.stopPropagation();
              handleCancel(task.id);
            }}
          />
        </Tooltip>,
      );
    }
    return actions;
  };

  const renderMeta = (task: TaskSnapshot) => {
    const progress = formatProgress(task);
    return (
      <Space orientation='vertical' size={2}>
        <Space>
          <Tag color={STATUS_COLOR[task.status]}>{task.status}</Tag>
          <Text type='secondary'>{task.title ?? task.type}</Text>
        </Space>
        {task.description && (
          <Text type='secondary' ellipsis>
            {task.description}
          </Text>
        )}
        {task.progress?.stage && <Text type='secondary'>{task.progress.stage}</Text>}
        {progress !== undefined && (
          <Progress
            percent={progress}
            size='small'
            status={task.status === 'FAILED' ? 'exception' : task.status === 'SUCCEEDED' ? 'success' : 'active'}
          />
        )}
        {task.error && (
          <Text type='danger' ellipsis>
            {task.error}
          </Text>
        )}
        <Text type='secondary' style={{ fontSize: 12 }}>
          {formatRelativeTime(task.createdAt)}
        </Text>
      </Space>
    );
  };

  return (
    <>
      <Tooltip title='Tasks'>
        <Badge count={activeCount} size='small'>
          <Button
            type='text'
            icon={activeCount > 0 ? <PlayCircleOutlined /> : <BellOutlined />}
            onClick={() => setOpen(true)}
          />
        </Badge>
      </Tooltip>

      <Drawer
        title={<Title level={4}>Tasks</Title>}
        open={open}
        onClose={() => setOpen(false)}
        size={420}
        destroyOnHidden
      >
        <Space style={{ marginBottom: 8 }}>
          <Button icon={<DisconnectOutlined />} onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button onClick={() => navigate('/tasks')}>Open tasks page</Button>
        </Space>

        {isFetching ? (
          <div>Loading...</div>
        ) : sortedTasks.length > 0 ? (
          <Space orientation='vertical' style={{ width: '100%' }}>
            {sortedTasks.map(task => (
              <div
                key={task.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  padding: '12px 0',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'default',
                }}
              >
                <div style={{ flex: 1 }}>{renderMeta(task)}</div>
                <div style={{ marginLeft: 16 }}>{renderActions(task)}</div>
              </div>
            ))}
          </Space>
        ) : (
          <Empty description='No tasks yet' />
        )}
      </Drawer>
    </>
  );
};
